import React, { useEffect, useMemo, useRef, useState } from 'react';
import { SwatchBook } from 'lucide-react';

const TIPO_OS_OPTIONS = [
  { label: 'Corretiva', value: 'N' },
  { label: 'Preventiva', value: 'S' },
  { label: 'Aferição', value: 'A' },
  { label: 'Desinstalação', value: 'D' },
  { label: 'Recarga', value: 'R' },
  { label: 'Balcão', value: 'B' },
];

const TipoOSOptionsButton = ({
  disabled,
  selectedValue,
  onChange,
  confirmed = false,
  /** @type {string[]|null} quando definido, só estes values (N,S,A,D,R,B) aparecem */
  allowedValues = null,
  needsAttention = false,
}) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  const selectedLabel = useMemo(() => {
    const opt = TIPO_OS_OPTIONS.find(o => o.value === selectedValue);
    return opt?.label || '';
  }, [selectedValue]);

  useEffect(() => {
    if (confirmed) setOpen(false);
  }, [confirmed]);

  useEffect(() => {
    if (!open) return;

    const handleMouseDown = (e) => {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [open]);

  const selectedLabelOrDefault = selectedLabel || (needsAttention ? '' : 'Corretiva');

  const optionsToShow =
    allowedValues == null
      ? TIPO_OS_OPTIONS
      : TIPO_OS_OPTIONS.filter((o) => allowedValues.includes(o.value));

  if (confirmed) {
    return (
      <div ref={wrapperRef} style={{ position: 'relative', flexShrink: 0 }}>
        <button
          type="button"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '4px',
            padding: '4px 8px',
            fontSize: '11px',
            fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
            fontWeight: 500,
            color: 'var(--text-muted, #4a5568)',
            background: 'transparent',
            border: 'none',
            borderRadius: '6px',
            cursor: 'default',
            transition: 'all 0.2s ease'
          }}
        >
          <i
            className="bi bi-check-circle-fill"
            style={{
              fontSize: '12px',
              lineHeight: 1,
              color: 'var(--text-muted, #4a5568)',
              background: 'transparent'
            }}
          ></i>
          <span style={{ background: 'transparent' }}>
            Tipo confirmado: {selectedLabelOrDefault}
          </span>
        </button>
      </div>
    );
  }

  return (
    <div ref={wrapperRef} style={{ position: 'relative', flexShrink: 0 }}>
      <button
        type="button"
        className="open-os-btn-action"
        disabled={disabled}
        onClick={() => {
          if (disabled) return;
          setOpen(prev => !prev);
        }}
        onMouseEnter={(e) => {
          if (disabled) return;
          e.target.style.background = '#5b6170';
        }}
        onMouseLeave={(e) => {
          if (disabled) return;
          e.target.style.background = '#6b7280';
        }}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '4px',
          padding: '6px 10px',
          fontSize: '11px',
          fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
          fontWeight: 600,
          color: disabled ? 'var(--text-muted, #9ca3af)' : '#ffffff',
          background: disabled ? 'var(--bg-surface-alt, #e5e7eb)' : needsAttention ? '#b45309' : '#6b7280',
          border: disabled
            ? '1px solid var(--border-subtle, #d1d5db)'
            : needsAttention
              ? '2px solid #f59e0b'
              : '1px solid rgba(213, 198, 255, 1)',
          borderRadius: '6px',
          cursor: disabled ? 'not-allowed' : 'pointer',
          transition: 'all 0.2s ease',
          boxShadow: disabled ? 'none' : '0 2px 6px rgba(124, 77, 255, 0.35)',
          // Mantém o "cinza inabilitado" consistente com os outros botões.
          opacity: disabled ? 0.6 : 1,
          whiteSpace: 'nowrap',
        }}
        title={
          needsAttention
            ? 'Tipo de OS: escolha um tipo permitido para este defeito'
            : selectedLabel
              ? `Tipo padrão: ${selectedLabel}`
              : 'Selecione o tipo de OS'
        }
      >
        <SwatchBook size={14} style={{ flexShrink: 0 }} />
        <span>Tipo de OS</span>
      </button>

      {open && !disabled && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            marginTop: '4px',
            background: 'var(--modal-bg, #ffffff)',
            border: '1px solid rgba(213, 198, 255, 1)',
            borderRadius: '6px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            zIndex: 1000,
            minWidth: '220px',
            overflow: 'hidden',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {optionsToShow.map((opt, idx) => {
            const isSelected = opt.value === selectedValue;
            const isLast = idx === optionsToShow.length - 1;
            return (
              <div
                key={opt.value}
                className="open-os-list-item"
                style={{
                  padding: '8px 12px',
                  cursor: 'pointer',
                  transition: 'background 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '12px',
                  color: 'var(--text-body, #2d3748)',
                  fontSize: '12px',
                  fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                  background: isSelected ? 'var(--bg-surface-alt, #f7fafc)' : 'transparent',
                  fontWeight: isSelected ? 600 : 500,
                  borderBottom: isLast ? 'none' : '1px solid var(--border-subtle, #f1f5f9)',
                }}
                onMouseEnter={(e) => {
                  if (!isSelected) e.currentTarget.style.background = 'var(--bg-surface-alt, #f7fafc)';
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) e.currentTarget.style.background = 'transparent';
                }}
                onClick={() => {
                  onChange && onChange(opt.value);
                  setOpen(false);
                }}
              >
                <span>{opt.label}</span>
                {isSelected && (
                  <i
                    className="bi bi-check-circle-fill"
                    style={{ fontSize: '16px', color: '#7c4dff', background: 'transparent' }}
                  ></i>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TipoOSOptionsButton;

